/* The landing page's globe, drawn on a canvas with no library (the same drawing as the planner's globe, v9/globe34.js):
   a dotted night globe that turns slowly, with every university as a small light and flights crossing it, from the
   cities students come from to the cities they study in (faint paths, a plane with a short trail, a ring where it
   lands). Drag to turn it; a tap anywhere tells the page where (the page finds the nearest university and calls
   target(), which draws a reticle there and a line to the page's panel). It zooms with the wheel, a trackpad's pinch,
   two fingers on a touch screen, and + and − (zoom()); never the page itself. Reduced motion: no turning, no planes,
   and the view jumps instead of flying. It draws only while it's on screen and the tab is visible. */
import { GLOBE_LAND } from '../pages/landing/globe/land';
import type { LL } from './globe-data';

const RAD = Math.PI / 180;
/* how far it zooms: 1 is the whole globe; a picked university is shown at 2.2 */
export const MIN_ZOOM = 1, MAX_ZOOM = 6;
type V3 = [number, number, number];
const vec = (lat: number, lon: number): V3 => { const p = lat * RAD, l = lon * RAD, c = Math.cos(p); return [c * Math.sin(l), Math.sin(p), c * Math.cos(l)]; };

let landXYZ: Float32Array | null = null;
/* the land points as unit vectors, rebuilt from the lattice (the planner's scripts/globe_land.mjs makes the bits) */
function land(): Float32Array {
  if (landXYZ) return landXYZ;
  const N = GLOBE_LAND.n, raw = atob(GLOBE_LAND.bits), golden = Math.PI * (3 - Math.sqrt(5)), out: number[] = [];
  for (let i = 0; i < N; i++) {
    if (!(raw.charCodeAt(i >> 3) & (1 << (i & 7)))) continue;
    const y = 1 - 2 * (i + 0.5) / N, r = Math.sqrt(1 - y * y), t = golden * i;
    const lat = Math.asin(y) / RAD, lon = Math.atan2(Math.sin(t) * r, Math.cos(t) * r) / RAD;
    out.push(...vec(lat, lon));
  }
  return (landXYZ = new Float32Array(out));
}

export type Layout = { fit: number; shiftX: number; shiftY: number };
type Flight = { a: V3; b: V3; om: number; so: number; H: number; t0: number; dur: number; to: LL };
type Fly = { t0: number; ms: number; lat0: number; lon0: number; z0: number; lat1: number; lon1: number; z1: number };

export class GlobeView {
  readonly el: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private reduced: boolean;
  private onPick: (ll: LL | null) => void;
  /* the view */
  private lat = 18; private lon = 10; private zoom = 1;
  private w = 0; private h = 0; private dpr = 1; private R = 0; private cx = 0; private cy = 0;
  private lay: Layout = { fit: 0.46, shiftX: 0, shiftY: 0 };
  private goal: Layout = { fit: 0.46, shiftX: 0, shiftY: 0 };
  /* what it shows */
  private dots = new Float32Array(0); private big = new Uint8Array(0);
  private origins: LL[] = []; private dests: LL[] = [];
  private flights: Flight[] = []; private landings: Array<{ at: V3; t0: number }> = [];
  private nextFlight = 0; private flightsOn = true;
  private target: V3 | null = null; private anchor: { x: number; y: number } | null = null;
  private spinOn = true;
  /* motion and input */
  private fly: Fly | null = null; private vlon = 0; private vlat = 0;
  private lastInput = 0; private raf = 0; private last = 0;
  private drag: { x: number; y: number; x0: number; y0: number; t0: number; moved: number; lt: number } | null = null;
  /* fingers on the canvas, for a two-finger pinch */
  private touches = new Map<number, [number, number]>(); private pinch: { d: number; z: number } | null = null; private pinched = false;
  private visible = true;
  private io: IntersectionObserver | null = null; private ro: ResizeObserver | null = null;
  private glow: Record<'beacon' | 'route' | 'white', HTMLCanvasElement>;

  constructor(canvas: HTMLCanvasElement, opts: { onPick: (ll: LL | null) => void; label: string }) {
    this.el = canvas;
    this.ctx = canvas.getContext('2d')!;
    this.onPick = opts.onPick;
    this.reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    canvas.tabIndex = 0;
    canvas.dataset.zoom = '1.00';   // how far it's zoomed in (for the tests)
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label', opts.label);
    this.glow = { beacon: sprite('rgba(87,199,255,.9)'), route: sprite('rgba(239,93,224,.9)'), white: sprite('rgba(255,255,255,.95)') };
    canvas.addEventListener('pointerdown', this.down);
    canvas.addEventListener('pointermove', this.move);
    canvas.addEventListener('pointerup', this.up);
    canvas.addEventListener('pointercancel', this.cancel);
    canvas.addEventListener('keydown', this.key);
    canvas.addEventListener('wheel', this.wheel, { passive: false });
    document.addEventListener('visibilitychange', this.wake);
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(es => { this.visible = es.some(e => e.isIntersecting); if (this.visible) this.wake(); }, { threshold: 0 });
      this.io.observe(canvas);
    }
    if ('ResizeObserver' in window) { this.ro = new ResizeObserver(() => { this.draw(performance.now()); this.wake(); }); this.ro.observe(canvas); }
    this.wake();
  }

  destroy(): void {
    cancelAnimationFrame(this.raf); this.raf = 0;
    this.el.removeEventListener('pointerdown', this.down);
    this.el.removeEventListener('pointermove', this.move);
    this.el.removeEventListener('pointerup', this.up);
    this.el.removeEventListener('pointercancel', this.cancel);
    this.el.removeEventListener('keydown', this.key);
    this.el.removeEventListener('wheel', this.wheel);
    document.removeEventListener('visibilitychange', this.wake);
    this.io?.disconnect(); this.ro?.disconnect();
  }

  /* every university as [lat, lon]; ranked ones a little brighter */
  setSchools(points: readonly LL[], ranked: readonly boolean[]): void {
    this.dots = new Float32Array(points.length * 3); this.big = new Uint8Array(points.length);
    points.forEach((p, i) => { this.dots.set(vec(p[0], p[1]), i * 3); this.big[i] = ranked[i] ? 1 : 0; });
    this.wake();
  }
  /* where flights leave from and land */
  setFlights(origins: LL[], dests: LL[]): void { this.origins = origins; this.dests = dests; this.wake(); }
  flightsEnabled(on: boolean): void { this.flightsOn = on; if (!on) this.flights = []; this.wake(); }
  /* how big the globe is and where it sits; it moves there smoothly */
  layout(l: Layout, now = false): void { this.goal = { ...l }; if (now || this.reduced) this.lay = { ...l }; this.wake(); }
  spin(on: boolean): void { this.spinOn = on; this.wake(); }
  setTarget(ll: LL | null): void { this.target = ll ? vec(ll[0], ll[1]) : null; this.wake(); }
  /* zoom by a factor, towards a point on the canvas (the view turns a little towards the spot under it as it zooms in) */
  zoomBy(f: number, at?: [number, number] | null): void {
    const z0 = this.zoom, z1 = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z0 * f));
    if (Math.abs(z1 - z0) < 1e-4) return;
    if (at && z1 > z0) {
      const ll = this.fromScreen(at[0], at[1]);
      if (ll) { const k = 1 - z0 / z1; this.lat = Math.max(-70, Math.min(78, this.lat + (ll[0] - this.lat) * k)); this.lon = wrap(this.lon + wrap(ll[1] - this.lon) * k); }
    }
    this.zoom = z1; this.fly = null; this.vlon = this.vlat = 0; this.lastInput = performance.now(); this.wake();
    this.el.dataset.zoom = z1.toFixed(2);
    if (this.reduced) this.draw(performance.now());
  }
  /* the point (in the canvas) the line from the target goes to, or null for no line */
  setAnchor(p: { x: number; y: number } | null): void { this.anchor = p; this.wake(); }
  flyTo(lat: number, lon: number, zoom: number, ms = 1400): void {
    this.fly = { t0: performance.now(), ms: this.reduced ? 1 : ms, lat0: this.lat, lon0: this.lon, z0: this.zoom, lat1: Math.max(-70, Math.min(78, lat)), lon1: lon, z1: zoom };
    this.lastInput = performance.now(); this.wake();
  }
  view() { return { lat: this.lat, lon: this.lon, zoom: this.zoom, R: this.R, cx: this.cx, cy: this.cy, w: this.w, h: this.h }; }
  /* a spot on the canvas, or behind the globe */
  project(lat: number, lon: number): { x: number; y: number; front: boolean } {
    const v = vec(lat, lon), s = this.toScreen(v, 1);
    return { x: s[0], y: s[1], front: s[2] > 0 };
  }
  /* a point on the canvas back to [lat, lon], or null off the globe */
  fromScreen(px: number, py: number): LL | null {
    if (!this.R && !this.size()) return null;   // nothing drawn yet (a tab in the background): measure now
    const x2 = (px - this.cx) / this.R, y2 = (this.cy - py) / this.R, d = x2 * x2 + y2 * y2;
    if (d > 1) return null;
    const z2 = Math.sqrt(1 - d), l = this.lon * RAD, p = this.lat * RAD, cl = Math.cos(l), sl = Math.sin(l), cp = Math.cos(p), sp = Math.sin(p);
    const y1 = y2 * cp + z2 * sp, z1 = -y2 * sp + z2 * cp, x1 = x2;
    const x = x1 * cl + z1 * sl, z = z1 * cl - x1 * sl;
    return [Math.asin(Math.max(-1, Math.min(1, y1))) / RAD, Math.atan2(x, z) / RAD];
  }

  /* ---------- drawing ---------- */
  private toScreen(v: V3, lift: number): V3 {
    const l = this.lon * RAD, p = this.lat * RAD, cl = Math.cos(l), sl = Math.sin(l), cp = Math.cos(p), sp = Math.sin(p);
    const x1 = v[0] * cl - v[2] * sl, z1 = v[2] * cl + v[0] * sl, y2 = v[1] * cp - z1 * sp, z2 = v[1] * sp + z1 * cp;
    const k = this.R * lift;
    return [this.cx + x1 * k, this.cy - y2 * k, z2];
  }
  private size(): boolean {
    const w = this.el.clientWidth, h = this.el.clientHeight;
    if (!w || !h) return false;
    const dpr = Math.min(w < 700 ? 1.75 : 2, window.devicePixelRatio || 1);
    if (w !== this.w || h !== this.h || dpr !== this.dpr) {
      this.w = w; this.h = h; this.dpr = dpr;
      this.el.width = Math.round(w * dpr); this.el.height = Math.round(h * dpr);
    }
    this.R = Math.min(w, h) * this.lay.fit * this.zoom;
    this.cx = w / 2 + this.lay.shiftX * w; this.cy = h / 2 + this.lay.shiftY * h;
    return true;
  }
  private draw(now: number): void {
    if (!this.size()) return;
    const { ctx, w, h, dpr, R, cx, cy } = this;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    /* air, the body, the rim */
    const air = ctx.createRadialGradient(cx, cy, R * 0.94, cx, cy, R * 1.3);
    air.addColorStop(0, 'rgba(87,199,255,.18)'); air.addColorStop(1, 'rgba(87,199,255,0)');
    ctx.fillStyle = air; ctx.beginPath(); ctx.arc(cx, cy, R * 1.3, 0, 7); ctx.fill();
    const body = ctx.createRadialGradient(cx - R * 0.35, cy - R * 0.42, R * 0.08, cx, cy, R);
    body.addColorStop(0, '#18233D'); body.addColorStop(1, '#090F1E');
    ctx.fillStyle = body; ctx.beginPath(); ctx.arc(cx, cy, R, 0, 7); ctx.fill();
    ctx.strokeStyle = 'rgba(127,149,214,.34)'; ctx.lineWidth = 1; ctx.stroke();
    this.drawGrid();
    /* land: dots in four depth bands, so the globe reads as round */
    const L = land(), s = Math.max(1.3, Math.min(3.2, R / 160)), half = s / 2;
    const bands = [new Path2D(), new Path2D(), new Path2D(), new Path2D()];
    const l = this.lon * RAD, p = this.lat * RAD, cl = Math.cos(l), sl = Math.sin(l), cp = Math.cos(p), sp = Math.sin(p);
    for (let i = 0; i < L.length; i += 3) {
      const x = L[i], y = L[i + 1], z = L[i + 2];
      const z1 = z * cl + x * sl, z2 = y * sp + z1 * cp;
      if (z2 <= 0.02) continue;
      const px = cx + (x * cl - z * sl) * R, py = cy - (y * cp - z1 * sp) * R;
      if (px < -4 || py < -4 || px > w + 4 || py > h + 4) continue;
      bands[z2 > 0.72 ? 0 : z2 > 0.45 ? 1 : z2 > 0.2 ? 2 : 3].rect(px - half, py - half, s, s);
    }
    ctx.fillStyle = '#9DB2F0';
    [0.92, 0.7, 0.48, 0.24].forEach((a, k) => { ctx.globalAlpha = a; ctx.fill(bands[k]); });
    /* the universities */
    const D = this.dots, dz = Math.min(2, 0.8 + this.zoom * 0.35);
    ctx.fillStyle = '#57C7FF';
    for (let i = 0; i < D.length; i += 3) {
      const sc = this.toScreen([D[i], D[i + 1], D[i + 2]], 1);
      if (sc[2] <= 0.05 || sc[0] < -4 || sc[1] < -4 || sc[0] > w + 4 || sc[1] > h + 4) continue;
      const b = this.big[i / 3], r = (b ? 1.6 : 1.05) * dz;
      ctx.globalAlpha = (b ? 0.95 : 0.55) * Math.min(1, 0.3 + sc[2]);
      ctx.fillRect(sc[0] - r, sc[1] - r, r * 2, r * 2);
    }
    ctx.globalAlpha = 1;
    this.drawFlights(now);
    this.drawTarget(now);
  }
  /* a faint grid every 30 degrees: the instrument look */
  private drawGrid(): void {
    const { ctx } = this;
    ctx.strokeStyle = 'rgba(127,149,214,.09)'; ctx.lineWidth = 1;
    const line = (pts: V3[]) => {
      ctx.beginPath(); let pen = false;
      for (const v of pts) { const sc = this.toScreen(v, 1); if (sc[2] > 0) { if (pen) ctx.lineTo(sc[0], sc[1]); else ctx.moveTo(sc[0], sc[1]); pen = true; } else pen = false; }
      ctx.stroke();
    };
    for (let lat = -60; lat <= 60; lat += 30) { const pts: V3[] = []; for (let lon = -180; lon <= 180; lon += 4) pts.push(vec(lat, lon)); line(pts); }
    for (let lon = -180; lon < 180; lon += 30) { const pts: V3[] = []; for (let lat = -84; lat <= 84; lat += 4) pts.push(vec(lat, lon)); line(pts); }
  }
  /* flights: a faint raised path, a plane with a short trail, a ring where it lands */
  private drawFlights(now: number): void {
    const { ctx } = this;
    if (this.flightsOn && this.origins.length && this.dests.length) this.spawn(now);
    /* with reduced motion the paths stay, still */
    if (!this.reduced) this.flights = this.flights.filter(f => now - f.t0 < f.dur + 1100);
    for (const f of this.flights) {
      const age = now - f.t0, p = this.reduced ? 1 : Math.min(1, age / f.dur), e = ease(p);
      const fadeIn = this.reduced ? 1 : Math.min(1, age / 500), fadeOut = age > f.dur ? Math.max(0, 1 - (age - f.dur) / 1100) : 1;
      const pts: Array<[number, number, boolean]> = [];
      const N = 56;
      for (let k = 0; k <= N; k++) {
        const t = k / N, f1 = Math.sin((1 - t) * f.om) / f.so, f2 = Math.sin(t * f.om) / f.so, lift = 1 + f.H * Math.sin(Math.PI * t);
        const v: V3 = [f.a[0] * f1 + f.b[0] * f2, f.a[1] * f1 + f.b[1] * f2, f.a[2] * f1 + f.b[2] * f2];
        const sc = this.toScreen(v, lift), dx = (sc[0] - this.cx) / this.R, dy = (sc[1] - this.cy) / this.R;
        pts.push([sc[0], sc[1], sc[2] >= 0 || dx * dx + dy * dy >= 1]);
      }
      /* the whole path, faint */
      const s0 = pts[0], s1 = pts[N], gr = ctx.createLinearGradient(s0[0], s0[1], s1[0], s1[1]);
      gr.addColorStop(0, 'rgba(110,155,255,1)'); gr.addColorStop(1, 'rgba(239,93,224,1)');
      ctx.strokeStyle = gr; ctx.lineWidth = 1.3; ctx.globalAlpha = 0.3 * fadeIn * fadeOut;
      stroke(ctx, pts, 0, N);
      if (this.reduced) continue;
      /* the trail and the plane */
      const head = e * N, tail = Math.max(0, head - N * 0.16), hi = Math.floor(head);
      if (p < 1) {
        const hp = interp(pts, head), bp = interp(pts, Math.max(0, head - 0.6)), tp = interp(pts, tail);
        const tg = ctx.createLinearGradient(tp[0], tp[1], hp[0], hp[1]);
        tg.addColorStop(0, 'rgba(125,213,255,0)'); tg.addColorStop(1, 'rgba(255,255,255,.95)');
        ctx.strokeStyle = tg; ctx.globalAlpha = fadeIn; ctx.lineWidth = 2.4;
        stroke(ctx, pts, Math.floor(tail), hi, [hp]);
        if (hp[2]) {
          ctx.globalAlpha = 0.9 * fadeIn; ctx.drawImage(this.glow.white, hp[0] - 12, hp[1] - 12, 24, 24);
          plane(ctx, hp[0], hp[1], Math.atan2(hp[1] - bp[1], hp[0] - bp[0]));
        }
      } else if (!this.landings.some(x => x.at === f.b)) this.landings.push({ at: f.b, t0: f.t0 + f.dur });
    }
    /* where planes landed: a ring that spreads and fades */
    this.landings = this.landings.filter(x => now - x.t0 < 1200);
    for (const x of this.landings) {
      const sc = this.toScreen(x.at, 1); if (sc[2] <= 0.05) continue;
      const k = (now - x.t0) / 1200;
      ctx.globalAlpha = (1 - k) * 0.85; ctx.strokeStyle = '#EF5DE0'; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.arc(sc[0], sc[1], 3 + k * 16, 0, 7); ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }
  private spawn(now: number): void {
    const max = this.w < 700 ? 7 : 11;
    let tries = 0;
    while (this.flights.length < max && (this.reduced || now >= this.nextFlight) && tries++ < 30) {
      const from = this.origins[Math.floor(Math.random() * this.origins.length)], to = this.dests[Math.floor(Math.random() * this.dests.length)];
      const a = vec(from[0], from[1]), b = vec(to[0], to[1]);
      const om = Math.acos(Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2])));
      if (om < 0.25 || om > 2.6) continue;
      const so = Math.sin(om);
      this.flights.push({ a, b, om, so, H: 0.05 + 0.18 * om / Math.PI, t0: now, dur: 3800 + Math.random() * 2600, to });
      this.nextFlight = now + (this.flights.length < 3 ? 120 : 420 + Math.random() * 760);
    }
  }
  /* the picked university: a reticle, and a line to the page's panel */
  private drawTarget(now: number): void {
    if (!this.target) return;
    const { ctx } = this, sc = this.toScreen(this.target, 1);
    if (sc[2] <= 0.02) return;
    const [x, y] = sc, pulse = this.reduced ? 0.4 : (now % 1700) / 1700, spin = this.reduced ? 0 : now / 2600;
    if (this.anchor) {
      const ax = this.anchor.x, ay = this.anchor.y, ex = x + (ax - x) * 0.32;
      ctx.strokeStyle = 'rgba(87,199,255,.55)'; ctx.lineWidth = 1; ctx.setLineDash([4, 4]);
      ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(ex, ay); ctx.lineTo(ax, ay); ctx.stroke(); ctx.setLineDash([]);
      ctx.fillStyle = '#57C7FF'; ctx.beginPath(); ctx.arc(ax, ay, 2.5, 0, 7); ctx.fill();
    }
    ctx.globalAlpha = 0.85; ctx.drawImage(this.glow.route, x - 26, y - 26, 52, 52); ctx.globalAlpha = 1;
    ctx.strokeStyle = `rgba(239,93,224,${(1 - pulse) * 0.9})`; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(x, y, 9 + pulse * 26, 0, 7); ctx.stroke();
    /* four corner brackets, turning slowly */
    ctx.strokeStyle = 'rgba(87,199,255,.95)'; ctx.lineWidth = 1.6;
    for (let q = 0; q < 4; q++) {
      const a = spin + q * Math.PI / 2;
      ctx.beginPath(); ctx.arc(x, y, 22, a + 0.2, a + 0.75); ctx.stroke();
    }
    /* ticks to the four sides */
    ctx.strokeStyle = 'rgba(237,241,250,.7)'; ctx.lineWidth = 1;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) { ctx.beginPath(); ctx.moveTo(x + dx * 28, y + dy * 28); ctx.lineTo(x + dx * 38, y + dy * 38); ctx.stroke(); }
    ctx.fillStyle = '#EF5DE0'; ctx.beginPath(); ctx.arc(x, y, 5.5, 0, 7); ctx.fill();
    ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(x, y, 2.2, 0, 7); ctx.fill();
  }

  /* ---------- motion ---------- */
  private spinning(): boolean {
    return this.spinOn && !this.reduced && !this.target && !this.drag && this.zoom < 1.05 && performance.now() - this.lastInput > 2600;
  }
  private animating(): boolean {
    if (this.reduced) return !!this.fly;
    return true;   // flights, the reticle and the slow turn keep it moving while it's on screen
  }
  private frame = (now: number): void => {
    this.raf = 0;
    if (!this.el.isConnected || document.hidden || !this.visible) return;
    const dt = Math.min(64, now - (this.last || now)); this.last = now;
    /* the layout eases to where the page wants it */
    const k = 1 - Math.exp(-dt / 170);
    this.lay = { fit: this.lay.fit + (this.goal.fit - this.lay.fit) * k, shiftX: this.lay.shiftX + (this.goal.shiftX - this.lay.shiftX) * k, shiftY: this.lay.shiftY + (this.goal.shiftY - this.lay.shiftY) * k };
    if (this.fly) {
      const f = this.fly, t = Math.max(0, Math.min(1, (now - f.t0) / f.ms)), e = ease(t);   // a frame's time can be a little before the flight began
      this.lat = f.lat0 + (f.lat1 - f.lat0) * e; this.lon = f.lon0 + wrap(f.lon1 - f.lon0) * e; this.zoom = f.z0 + (f.z1 - f.z0) * e;
      if (t >= 1) { this.fly = null; this.el.dataset.zoom = this.zoom.toFixed(2); }
    } else if (!this.drag) {
      if (Math.abs(this.vlon) > 0.002 || Math.abs(this.vlat) > 0.002) {
        this.lon += this.vlon * dt; this.lat = Math.max(-70, Math.min(78, this.lat + this.vlat * dt));
        const decay = Math.pow(0.94, dt / 16); this.vlon *= decay; this.vlat *= decay;
      } else if (this.spinning()) this.lon += 0.0042 * dt;
    }
    this.lon = wrap(this.lon);
    this.draw(now);
    if (this.animating()) this.raf = requestAnimationFrame(this.frame);
  };
  private wake = (): void => {
    if (this.raf || !this.el.isConnected) return;
    this.raf = requestAnimationFrame(t => { this.last = t; this.frame(t); });
    if (this.reduced) this.draw(performance.now());
  };

  /* ---------- input ---------- */
  private pos(e: MouseEvent): [number, number] { const b = this.el.getBoundingClientRect(); return [e.clientX - b.left, e.clientY - b.top]; }
  /* the wheel and a trackpad's pinch zoom the globe (towards the cursor), not the page. A pinch (which browsers send as
     the wheel with Ctrl) always zooms. The plain wheel zooms while the page is at its top or the globe is zoomed in:
     up zooms in, down zooms out, and down at full size moves down the page as usual. */
  private wheel = (e: WheelEvent): void => {
    const pinch = e.ctrlKey || e.metaKey;
    const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaMode === 2 ? e.deltaY * 400 : e.deltaY;
    if (!dy) return;
    const zoomedIn = this.zoom > MIN_ZOOM + 0.01;
    if (!pinch) {
      const top = (document.scrollingElement?.scrollTop ?? window.scrollY) < 4;
      if (!zoomedIn && (dy > 0 || !top)) return;   // the page scrolls
      if (dy < 0 && this.zoom >= MAX_ZOOM - 0.01) return;
    }
    e.preventDefault();
    this.zoomBy(Math.exp(-dy * (pinch ? 0.01 : 0.0015)), this.pos(e));
  };
  private down = (e: PointerEvent): void => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    if (e.pointerType !== 'mouse') {
      this.touches.set(e.pointerId, this.pos(e));
      if (this.touches.size === 2) {
        const [a, b] = [...this.touches.values()];
        this.pinch = { d: Math.max(1, Math.hypot(a[0] - b[0], a[1] - b[1])), z: this.zoom }; this.pinched = true; this.drag = null;
        return;
      }
      if (this.touches.size > 2) return;
    }
    const [x, y] = this.pos(e);
    this.drag = { x, y, x0: x, y0: y, t0: performance.now(), moved: 0, lt: performance.now() };
    this.fly = null; this.vlon = this.vlat = 0; this.lastInput = performance.now();
    if (e.pointerType === 'mouse') this.el.setPointerCapture?.(e.pointerId);
  };
  private move = (e: PointerEvent): void => {
    if (this.touches.has(e.pointerId)) this.touches.set(e.pointerId, this.pos(e));
    if (this.pinch && this.touches.size >= 2) {
      const [a, b] = [...this.touches.values()], d = Math.hypot(a[0] - b[0], a[1] - b[1]);
      const want = this.pinch.z * d / this.pinch.d, mid: [number, number] = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      this.zoomBy(want / this.zoom, mid);
      return;
    }
    const d = this.drag; if (!d) return;
    const [x, y] = this.pos(e), dx = x - d.x, dy = y - d.y, now = performance.now(), dt = Math.max(1, now - d.lt);
    d.moved += Math.abs(dx) + Math.abs(dy);
    if (d.moved < 4) return;
    const k = 1 / this.R / RAD;
    /* on a touch screen, an up-and-down swipe scrolls the page (touch-action: pan-y); sideways turns the globe */
    const touch = e.pointerType !== 'mouse';
    this.lon = wrap(this.lon - dx * k); if (!touch) this.lat = Math.max(-70, Math.min(78, this.lat + dy * k));
    this.vlon = -dx * k / dt; this.vlat = touch ? 0 : dy * k / dt;
    d.x = x; d.y = y; d.lt = now; this.lastInput = now;
    this.el.style.cursor = 'grabbing';
    this.wake();
  };
  private up = (e: PointerEvent): void => {
    this.touches.delete(e.pointerId);
    if (this.touches.size < 2) this.pinch = null;
    if (this.pinched) { if (!this.touches.size) this.pinched = false; this.drag = null; this.wake(); return; }   // a pinch is never a tap
    const d = this.drag; if (!d) return;
    this.drag = null; this.el.style.cursor = '';
    if (d.moved < 8 && performance.now() - d.t0 < 600) { this.vlon = this.vlat = 0; this.onPick(this.fromScreen(d.x0, d.y0)); }
    else if (performance.now() - d.lt > 80) this.vlon = this.vlat = 0;
    this.lastInput = performance.now();
    if (e.pointerType === 'mouse') this.el.releasePointerCapture?.(e.pointerId);
    this.wake();
  };
  private cancel = (e: PointerEvent): void => {
    this.touches.delete(e.pointerId); if (this.touches.size < 2) this.pinch = null; if (!this.touches.size) this.pinched = false;
    this.drag = null; this.el.style.cursor = ''; this.wake();
  };
  private key = (e: KeyboardEvent): void => {
    const k = e.key, step = 9 / this.zoom;
    if (k === 'ArrowLeft') this.lon = wrap(this.lon - step);
    else if (k === 'ArrowRight') this.lon = wrap(this.lon + step);
    else if (k === 'ArrowUp') this.lat = Math.min(78, this.lat + step);
    else if (k === 'ArrowDown') this.lat = Math.max(-70, this.lat - step);
    else if (k === 'Enter' || k === ' ') this.onPick([this.lat, this.lon]);
    else if (k === '+' || k === '=') { e.preventDefault(); this.zoomBy(1.25); return; }
    else if (k === '-' || k === '_') { e.preventDefault(); this.zoomBy(0.8); return; }
    else return;
    e.preventDefault(); this.fly = null; this.lastInput = performance.now(); this.wake();
  };
}

/* ---------- helpers ---------- */
const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const wrap = (d: number) => ((d + 540) % 360) - 180;
function sprite(color: string): HTMLCanvasElement {
  const c = document.createElement('canvas'); c.width = c.height = 64;
  const g = c.getContext('2d')!, gr = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  gr.addColorStop(0, color); gr.addColorStop(0.35, color.replace(/[\d.]+\)$/, '.35)')); gr.addColorStop(1, color.replace(/[\d.]+\)$/, '0)'));
  g.fillStyle = gr; g.fillRect(0, 0, 64, 64); return c;
}
/* a point part-way along the path (k in path steps) */
function interp(pts: Array<[number, number, boolean]>, k: number): [number, number, boolean] {
  const i = Math.min(pts.length - 2, Math.floor(k)), f = k - i, a = pts[i], b = pts[i + 1];
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] && b[2]];
}
/* the visible parts of a path, from step i to j, plus any extra points at the end */
function stroke(ctx: CanvasRenderingContext2D, pts: Array<[number, number, boolean]>, i: number, j: number, extra: Array<[number, number, boolean]> = []): void {
  ctx.beginPath(); let pen = false;
  for (const p of pts.slice(i, j + 1).concat(extra)) { if (p[2]) { if (pen) ctx.lineTo(p[0], p[1]); else ctx.moveTo(p[0], p[1]); pen = true; } else pen = false; }
  ctx.stroke();
}
/* a small plane pointing along its path */
function plane(ctx: CanvasRenderingContext2D, x: number, y: number, a: number): void {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a); ctx.scale(1.3, 1.3); ctx.globalAlpha = 1; ctx.fillStyle = '#FFFFFF';
  ctx.beginPath();
  ctx.moveTo(6.5, 0); ctx.lineTo(1.5, -1.1); ctx.lineTo(-1, -5.5); ctx.lineTo(-2.4, -5.5); ctx.lineTo(-0.9, -1.1); ctx.lineTo(-4.2, -0.9);
  ctx.lineTo(-5.4, -2.6); ctx.lineTo(-6.3, -2.6); ctx.lineTo(-5.6, 0); ctx.lineTo(-6.3, 2.6); ctx.lineTo(-5.4, 2.6); ctx.lineTo(-4.2, 0.9);
  ctx.lineTo(-0.9, 1.1); ctx.lineTo(-2.4, 5.5); ctx.lineTo(-1, 5.5); ctx.lineTo(1.5, 1.1); ctx.closePath();
  ctx.fill(); ctx.restore();
}
